/**
 * Subtle "go fullscreen" affordance for browser-tab viewers.
 *
 * Streamers viewing the browser source in a normal Chrome/Firefox/Edge tab
 * see a small button in the top-right that triggers the Fullscreen API.
 * Hides itself once already fullscreen, and stays hidden inside OBS where
 * the browser source is already rendered full-canvas.
 *
 * OBS detection: OBS injects `window.obsstudio` into pages it loads. Several
 * OBS forks also stamp the user-agent with `OBS/`. We check both.
 */

const BUTTON_ID = 'sg-fullscreen-btn';

function isObs(): boolean {
  if (typeof window === 'undefined') return false;
  if ((window as unknown as { obsstudio?: unknown }).obsstudio) return true;
  if (typeof navigator !== 'undefined' && /OBS\//i.test(navigator.userAgent)) return true;
  return false;
}

function isFullscreen(): boolean {
  return Boolean(document.fullscreenElement);
}

function ensureStyles(): void {
  if (document.getElementById('sg-fullscreen-btn-style')) return;
  const s = document.createElement('style');
  s.id = 'sg-fullscreen-btn-style';
  s.textContent = `
    #${BUTTON_ID} {
      position: fixed;
      top: 12px;
      right: 12px;
      z-index: 9999;
      padding: 8px 14px;
      background: rgba(0, 0, 0, 0.75);
      color: #ffd54a;
      border: 1px solid rgba(255, 213, 74, 0.7);
      border-radius: 6px;
      font: 700 13px/1 'Inter', system-ui, sans-serif;
      letter-spacing: 0.06em;
      cursor: pointer;
      opacity: 0.85;
      transition: opacity 120ms ease, background 120ms ease;
      user-select: none;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
    }
    #${BUTTON_ID}:hover {
      opacity: 1;
      background: rgba(0, 0, 0, 0.95);
    }
    #${BUTTON_ID}[hidden] {
      display: none;
    }
  `;
  document.head.appendChild(s);
}

export function mountFullscreenButton(): HTMLButtonElement | null {
  if (typeof document === 'undefined') return null;
  if (isObs()) return null; // OBS already renders full canvas — no button needed.
  if (document.getElementById(BUTTON_ID)) {
    return document.getElementById(BUTTON_ID) as HTMLButtonElement;
  }

  ensureStyles();
  const btn = document.createElement('button');
  btn.id = BUTTON_ID;
  btn.type = 'button';
  btn.textContent = 'FULLSCREEN';
  btn.setAttribute('data-testid', 'fullscreen-button');
  btn.title = 'Toggle fullscreen (or press F11)';

  btn.addEventListener('click', () => {
    if (isFullscreen()) {
      void document.exitFullscreen?.();
    } else {
      void document.documentElement.requestFullscreen?.().catch(() => {
        // Some browsers reject if not from a user gesture; the click IS one,
        // so this should rarely fire. Swallow silently.
      });
    }
  });

  function syncVisibility(): void {
    btn.hidden = isFullscreen();
  }
  document.addEventListener('fullscreenchange', syncVisibility);
  syncVisibility();

  // Mount inside the stage backdrop if available (sits over letterbox bars),
  // else fall back to body.
  const bg = document.getElementById('sg-stage-bg');
  (bg ?? document.body).appendChild(btn);

  return btn;
}
